import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EditBlog = () => {
    const { id } = useParams(); // Get the ID from the URL
    const navigate = useNavigate();
    const quillRef = useRef(null);

    // Form States
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [price, setPrice] = useState('');

    // Files (New Uploads)
    const [imageFile, setImageFile] = useState(null);
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [pdfFile, setPdfFile] = useState(null);
    const [videoFile, setVideoFile] = useState(null);

    // Existing Data (From Database) - To preserve data if not changed
    const [existingImage, setExistingImage] = useState(null);
    const [existingGallery, setExistingGallery] = useState([]);
    const [existingPdf, setExistingPdf] = useState(null);
    const [existingVideo, setExistingVideo] = useState(null);

    // Toggles & Links
    const [isVideo, setIsVideo] = useState(false);
    const [isForm, setIsForm] = useState(false);
    const [paymentLink, setPaymentLink] = useState('');
    const [linkName, setLinkName] = useState('');
    const [link, setLink] = useState('');

    // UI States
    const [isLoading, setIsLoading] = useState(true); // Start loading true to fetch data
    const [status, setStatus] = useState('');

    const inputStyles = "bg-gray-900 border border-gray-700 text-gray-200 p-3 rounded-lg w-full focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-300 shadow-sm";
    const labelStyles = "block text-gray-300 text-sm font-semibold mb-2";

    // 1. Fetch Categories and Blog Details
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Categories
                const catRes = await axiosInstance.get('/categories');
                setCategories(catRes.data);

                // Fetch Blog Details
                const blogRes = await axiosInstance.get(`/blogs/${id}`);
                const data = blogRes.data;

                // Populate State
                setTitle(data.title);
                setContent(data.content);
                setSelectedCategory(data.category_id);
                setPrice(data.price || '');
                setPaymentLink(data.payment_link || '');
                
                // Populate Toggles
                setIsVideo(!!data.isVideo); 
                // Check if form data exists to toggle isForm
                if (data.form_name || data.form_link) {
                    setIsForm(true);
                    setLinkName(data.form_name || '');
                    setLink(data.form_link || '');
                }

                // Populate Existing Media URLs
                setExistingImage(data.image);
                setExistingGallery(data.images || []); // Assuming backend returns array of strings
                setExistingPdf(data.pdf_link);
                setExistingVideo(data.video_link);

                setIsLoading(false);
            } catch (error) {
                console.error("Failed to fetch data", error);
                setStatus('Failed to load blog data.');
                setIsLoading(false);
            }
        };

        fetchData();
    }, [id]);

    // Quill Image Handler
    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            const formData = new FormData();
            formData.append('image', file);
            try {
                const response = await axiosInstance.post('/blogs/upload-image-cloudinary', formData);
                const imageUrl = response.data.url;
                const quill = quillRef.current.getEditor();
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', imageUrl);
            } catch (error) {
                console.error('Quill image upload failed', error);
            }
        };
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, false] }],
                ['bold', 'italic', 'underline'],
                ['link', 'image']
            ],
            handlers: { image: imageHandler },
        },
    }), []);

    const handleGalleryChange = (e) => {
        if (e.target.files) {
            setGalleryFiles(Array.from(e.target.files));
        }
    };

    // 2. Handle Update Submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setStatus('Preparing update...');

        let finalImageUrl = existingImage;
        let finalPdfUrl = existingPdf;
        let finalVideoUrl = existingVideo;
        let finalGalleryUrls = [...existingGallery]; // Start with existing

        try {
            // 1. Upload NEW Featured Image (if selected)
            if (imageFile) {
                setStatus('Uploading new featured image...');
                const imageFormData = new FormData();
                imageFormData.append('image', imageFile);
                const imgRes = await axiosInstance.post('/blogs/upload-image-cloudinary', imageFormData);
                finalImageUrl = imgRes.data.url; // Replace existing
            }

            // 2. Upload NEW Gallery Images (if selected) and Append
            if (galleryFiles.length > 0) {
                setStatus(`Uploading ${galleryFiles.length} new gallery images...`);
                const uploadPromises = galleryFiles.map(file => {
                    const formData = new FormData();
                    formData.append('image', file);
                    return axiosInstance.post('/blogs/upload-image-cloudinary', formData);
                });

                const responses = await Promise.all(uploadPromises);
                const newUrls = responses.map(res => res.data.url);
                // Combine existing with new (or replace based on your preference. Here we combine)
                finalGalleryUrls = [...finalGalleryUrls, ...newUrls];
            }

            // 3. Upload NEW PDF (if selected)
            if (pdfFile) {
                setStatus('Uploading new PDF...');
                const pdfFormData = new FormData();
                pdfFormData.append('pdfFile', pdfFile);
                const pdfRes = await axiosInstance.post('/blogs/upload-pdf-local', pdfFormData);
                finalPdfUrl = pdfRes.data.link;
            }

            // 4. Upload NEW Video (if selected)
            if (isVideo && videoFile) {
                setStatus('Uploading new Video...');
                const videoFormData = new FormData();
                videoFormData.append('videoFile', videoFile);
                const videoRes = await axiosInstance.post('/blogs/upload-video-local', videoFormData);
                finalVideoUrl = videoRes.data.link;
            }

            // 5. Send PUT Request
            setStatus('Updating blog post...');
            const blogData = {
                title,
                content,
                category_id: selectedCategory,
                image: finalImageUrl,       
                images: finalGalleryUrls,   
                pdf_link: finalPdfUrl,
                isVideo: isVideo,
                video_link: isVideo ? finalVideoUrl : null, // Clear video if toggled off
                price: price,
                payment_link: paymentLink || null,
                form_name: isForm ? linkName : null,
                form_link: isForm ? link : null
            };

            await axiosInstance.put(`/blogs/${id}`, blogData);
            navigate('/admin/blogs-manage'); // Redirect back to manage page

        } catch (error) {
            console.error('Failed to update blog:', error);
            setStatus(`Error: ${error.response?.data?.error || 'Update failed.'}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading && !title) {
        return <div className="text-white text-center mt-10">Loading Data...</div>;
    }

    return (
        <div className="max-w-6xl mx-auto p-2">
            <form onSubmit={handleSubmit} className="bg-gray-800 p-8 rounded-2xl shadow-xl space-y-6">
                <h1 className="text-4xl font-bold mb-8 text-gray-100 text-center">Edit Blog / Product</h1>
                
                {/* Title */}
                <div>
                    <label className={labelStyles}>Title</label>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputStyles} required />
                </div>

                {/* Category */}
                <div>
                    <label className={labelStyles}>Category</label>
                    <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className={inputStyles + " bg-gray-900"} required>
                        <option value="" disabled>Select a category</option>
                        {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                    </select>
                </div>

                {/* Content */}
                <div>
                    <label className={labelStyles}>Description / Content</label>
                    <ReactQuill ref={quillRef} theme="snow" value={content} onChange={setContent} modules={modules} className="bg-gray-900 rounded-lg" />
                </div>

                {/* Price */}
                <div>
                    <label className={labelStyles}>Price (Optional)</label>
                    <input 
                        type="number" 
                        step="0.01" 
                        value={price} 
                        onChange={(e) => setPrice(e.target.value)} 
                        className={inputStyles} 
                        placeholder="0.00" 
                    />
                </div>

                {/* --- IMAGE UPLOAD SECTION --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Featured Image */}
                    <div>
                        <label className={labelStyles}>Featured Image (Main)</label>
                        {existingImage && (
                            <div className="mb-2">
                                <span className="text-xs text-green-400">Current Image:</span>
                                <img src={existingImage} alt="Current" className="h-20 w-auto rounded mt-1 object-cover border border-gray-600" />
                            </div>
                        )}
                        <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} className={inputStyles} />
                    </div>

                    {/* Gallery Images */}
                    <div>
                        <label className={labelStyles}>Gallery Images (Add More)</label>
                        {existingGallery && existingGallery.length > 0 && (
                            <div className="mb-2 flex flex-wrap gap-2">
                                <span className="w-full text-xs text-green-400">Existing Gallery:</span>
                                {existingGallery.map((img, idx) => (
                                    <img key={idx} src={img} alt="Gallery" className="h-12 w-12 rounded object-cover border border-gray-600" />
                                ))}
                            </div>
                        )}
                        <input 
                            type="file" 
                            accept="image/*" 
                            multiple 
                            onChange={handleGalleryChange} 
                            className={inputStyles} 
                        />
                        <p className="text-xs text-gray-400 mt-1">Select new images to append to the gallery.</p>
                        {galleryFiles.length > 0 && (
                            <p className="text-sm text-blue-400 mt-2">{galleryFiles.length} new images selected.</p>
                        )}
                    </div>
                </div>

                {/* PDF */}
                <div>
                    <label className={labelStyles}>Attach PDF</label>
                    {existingPdf && <p className="text-xs text-green-400 mb-1">Current PDF Attached</p>}
                    <input type="file" accept=".pdf" onChange={(e) => setPdfFile(e.target.files[0])} className={inputStyles} />
                </div>

                {/* Payment Link */}
                <div>
                    <label className={labelStyles}>Payment Link (Optional)</label>
                    <input type="url" value={paymentLink} onChange={(e) => setPaymentLink(e.target.value)} className={inputStyles} placeholder="https://example.com/payment" />
                </div>

                {/* Video Option */}
                <div className="p-4 border border-gray-700 rounded-lg bg-gray-900">
                    <label className={labelStyles}>Is this a video post?</label>
                    <div className="flex items-center space-x-6 text-gray-200">
                        <label className="cursor-pointer flex items-center space-x-2">
                            <input type="radio" name="isVideo" checked={isVideo === true} onChange={() => setIsVideo(true)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" />
                            <span>Yes</span>
                        </label>
                        <label className="cursor-pointer flex items-center space-x-2">
                            <input type="radio" name="isVideo" checked={isVideo === false} onChange={() => setIsVideo(false)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" />
                            <span>No</span>
                        </label>
                    </div>
                </div>

                {isVideo && (
                    <div className="space-y-4 animate-fade-in">
                        <div>
                            <label className={labelStyles}>Upload New Video File (Optional)</label>
                            {existingVideo && (
                                <div className="mb-2">
                                     <span className="text-xs text-green-400">Video currently attached. Upload new file to replace.</span>
                                </div>
                            )}
                            <input 
                                type="file" 
                                accept="video/mp4,video/x-m4v,video/*" 
                                onChange={(e) => setVideoFile(e.target.files[0])} 
                                className={inputStyles} 
                            />
                        </div>
                    </div>
                )}

                {/* Link Option */}
                <div className="p-4 border border-gray-700 rounded-lg bg-gray-900">
                    <label className={labelStyles}>Is there a link ?</label>
                    <div className="flex items-center space-x-6 text-gray-200">
                        <label className="cursor-pointer flex items-center space-x-2">
                            <input type="radio" name="isForm" checked={isForm === true} onChange={() => setIsForm(true)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" />
                            <span>Yes</span>
                        </label>
                        <label className="cursor-pointer flex items-center space-x-2">
                            <input type="radio" name="isForm" checked={isForm === false} onChange={() => setIsForm(false)} className="form-radio h-5 w-5 text-blue-500 bg-gray-800 border-gray-600 focus:ring-blue-500" />
                            <span>No</span>
                        </label>
                    </div>
                </div>

                {isForm && (
                    <div className="space-y-4 animate-fade-in">
                        <div>
                            <label className={labelStyles}>Name</label>
                            <input type="text" value={linkName} onChange={(e) => setLinkName(e.target.value)} className={inputStyles} placeholder="Name" required />
                        </div>
                        <div>
                            <label className={labelStyles}>Link</label>
                            <input type="url" value={link} onChange={(e) => setLink(e.target.value)} className={inputStyles} placeholder="https://example.com/link" required />
                        </div>
                    </div>
                )}

                <button type="submit" disabled={isLoading} className="w-full py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-semibold rounded-xl hover:from-teal-500 hover:to-green-500 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {isLoading && status.includes('Updating') ? status : 'Update Blog Post'}
                </button>

                {isLoading && status && <p className="text-center text-gray-300 mt-2">{status}</p>}
            </form>
             <style>{`
                [contentEditable][data-placeholder]:empty:before {
                  content: attr(data-placeholder);
                  color: #9ca3af; 
                  pointer-events: none;
                  display: block; 
                }
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.5s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default EditBlog;